import { Publish } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Autocomplete, Button, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ObjectSchema } from 'yup';
import { useAtomValue } from 'jotai';
import upperFirst from 'lodash-es/upperFirst';
import {
  ChangeEvent,
  FC,
  FocusEvent,
  FormEvent,
  SyntheticEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  createListDataModel,
  IListDataModel,
  ListSharingType,
} from '../../../../../../data-models/list.data-model';
import { activeCompanyListIdStateJ, listsByIdStateJ } from '../../../../../../services/state/ListsStateJ';
import { FormWrapper } from '../../../../../../pages/Finance/components/TransactionModal/Forms/TransactionForms/Equity/EquityBuyForm';
import { activeUsersAtom, usersByIdMapAtom } from '../../../../../../services/state/AppConfigStateJ';
import { CompanyAutocomplete } from '../../../../../CompanyAutocomplete/CompanyAutocomplete';
import { FieldGroup } from '../../../../../FieldGroup/FieldGroup';
import { FormField } from '../../../../../FormField/FormField';
import { useModalState } from '../../../../../Modal/ModalContext';
import { CompanyListSchema as validationSchema } from './CompanyListSchema';

const InnerFormWrapper = styled('div')<{ showBorder: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #ffffff;
  ${({ showBorder }) => showBorder && 'border: 1px solid #e0e0e0;'}
  ${({ showBorder }) => showBorder && '  padding: 0 2rem;'}
  border-radius: 4px;
`;

const ButtonWrapper = styled('div')`
  display: flex;
  gap: 12px;
  justify-content: flex-start;
  padding-bottom: 2rem;
`;

interface IProps {
  isEdit?: boolean;
  onSubmit: (list: Partial<IListDataModel>) => Promise<void>;
}

const sourceTypes = [
  { id: 1, value: 'Referral' },
  { id: 2, value: 'Direct' },
  { id: 3, value: 'Outbound' },
];

export const CompanyListForm: FC<IProps> = ({ isEdit = false, onSubmit }) => {
  const activeListId = useAtomValue(activeCompanyListIdStateJ);
  const listsMap = useAtomValue(listsByIdStateJ);
  const activeList = activeListId != null ? listsMap.get(activeListId) : null;

  const usersMap = useAtomValue(usersByIdMapAtom);
  const activeUsers = useAtomValue(activeUsersAtom);
  const allUserIds: number[] = useMemo(() => {
    return activeUsers.map((user) => user.id);
  }, [activeUsers]);

  const [touched, setTouched] = useState<Partial<Record<keyof IListDataModel, boolean>>>({});

  const {
    watch,
    setValue,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = useForm<Partial<IListDataModel>>({
    defaultValues: createListDataModel(isEdit ? { ...activeList } : {}),
    mode: 'all',
    resolver: yupResolver(validationSchema as ObjectSchema<Partial<IListDataModel>>),
  });

  const values = watch();

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setValue(e.target.name as keyof IListDataModel, e.target.value as never, { shouldValidate: true });
    },
    [setValue]
  );

  const handleBlur = useCallback((e: FocusEvent<HTMLElement>) => {
    const { name } = e.target as HTMLInputElement;
    if (name) {
      setTouched((prev) => ({ ...prev, [name]: true }));
    }
  }, []);

  const { onCloseModal } = useModalState();

  const handleError = (field: keyof IListDataModel) => {
    const message = errors[field as keyof typeof errors]?.message;
    return touched[field] && typeof message === 'string' ? message : '';
  };

  const onSharingWithChange = useCallback(
    (event: React.MouseEvent, value: string) => {
      setValue('shareWithTeamIds', []);
      setValue('shareWithUserIds', []);
      setValue('sharing', value as ListSharingType, { shouldValidate: true });
    },
    [setValue]
  );

  const onSourceCompanyChange = useCallback(
    (newValue: string | null) => {
      if (newValue === null) {
        return;
      }
      setValue('sourceCompany', newValue, { shouldValidate: true });
    },
    [setValue]
  );

  const onUsersChange = useCallback(
    (event: SyntheticEvent<Element, Event>, newValue: number[]) => {
      setValue('shareWithUserIds', newValue, { shouldValidate: true });
    },
    [setValue]
  );

  return (
    <FormWrapper
      onSubmit={(e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        handleSubmit(onSubmit)();
      }}
    >
      <InnerFormWrapper showBorder={!isEdit}>
        <FieldGroup hasBorder={false}>
          <FormField label={'List Name'} required error={handleError('name')}>
            <TextField
              name={'name'}
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              variant='outlined'
            />
          </FormField>
        </FieldGroup>
        <FieldGroup>
          <FormField label={'Source Type'} error={handleError('sourceType')}>
            <Autocomplete
              options={sourceTypes}
              disablePortal
              renderInput={(params) => <TextField {...params} name={`sourceType`} id={`sourceType`} />}
              getOptionLabel={(option) => option.value}
              value={sourceTypes?.find(({ value }) => value == values.sourceType) ?? null}
              onChange={(_, option) => setValue('sourceType', option?.value ?? '', { shouldValidate: true })}
              onFocus={() => setTouched((prev) => ({ ...prev, sourceType: true }))}
            />
          </FormField>
          <FormField label={'Internal Source'} error={handleError('internalSource')}>
            <Autocomplete
              options={allUserIds}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name={`internalSource`}
                  id={`internalSource`}
                  placeholder='Select Internal Source'
                  sx={{ marginTop: '0.5rem' }}
                />
              )}
              disablePortal
              value={Number(values.internalSource)}
              getOptionLabel={(id) => usersMap.get(Number(id))?.name || ''}
              isOptionEqualToValue={(id, value) => id === value}
              onChange={(_, option) => setValue('internalSource', option ?? null, { shouldValidate: true })}
              onFocus={() => setTouched((prev) => ({ ...prev, internalSource: true }))}
            />
          </FormField>
          <FormField label={'Source Company'} error={handleError('sourceCompany')}>
            <CompanyAutocomplete
              value={values.sourceCompany}
              onChange={onSourceCompanyChange}
              onBlur={handleBlur}
              onFocus={() => setTouched((prev) => ({ ...prev, sourceCompany: true }))}
            />
          </FormField>
          <FormField label={'Source'} error={handleError('source')}>
            <TextField
              name={'source'}
              value={values.source}
              onChange={handleChange}
              onBlur={handleBlur}
              variant='outlined'
            />
          </FormField>
        </FieldGroup>
        <FieldGroup>
          <FormField label={'Share with'} required>
            <ToggleButtonGroup
              aria-label='select share with'
              color={'secondary'}
              exclusive
              onChange={onSharingWithChange}
              value={values.sharing}
              fullWidth={true}
            >
              <ToggleButton value={ListSharingType.PRIVATE} aria-label='share with  none'>
                {upperFirst(ListSharingType.PRIVATE)}
              </ToggleButton>
              <ToggleButton value={ListSharingType.INDIVIDUAL} aria-label='share with individuals'>
                {upperFirst(ListSharingType.INDIVIDUAL)}
              </ToggleButton>
              <ToggleButton value={ListSharingType.PUBLIC} aria-label='share with everyone'>
                {upperFirst(ListSharingType.PUBLIC)}
              </ToggleButton>
            </ToggleButtonGroup>
            {values.sharing === ListSharingType.INDIVIDUAL && (
              <Autocomplete
                multiple
                options={allUserIds}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name={'shareWithUserIds'}
                    placeholder={`Select a user`}
                    sx={{ marginTop: '0.5rem' }}
                    size='small'
                  />
                )}
                disablePortal
                getOptionLabel={(option) => usersMap.get(option)?.name || ''}
                isOptionEqualToValue={(option, value) => option === value}
                value={values.shareWithUserIds ?? []}
                onChange={onUsersChange}
              />
            )}
          </FormField>
        </FieldGroup>
        <ButtonWrapper>
          <LoadingButton
            variant={'contained'}
            color='secondary'
            disabled={!isValid || isSubmitting}
            type={'submit'}
            loading={isSubmitting}
            endIcon={<Publish />}
            loadingPosition='end'
          >
            {isEdit ? 'Save' : 'Create List'}
          </LoadingButton>
          <Button variant={'outlined'} color='secondary' onClick={onCloseModal}>
            Cancel
          </Button>
        </ButtonWrapper>
      </InnerFormWrapper>
    </FormWrapper>
  );
};
